import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Input,
  InputNumber,
  ColorPicker,
  message,
  Space,
  Tag,
  Popconfirm,
  Empty,
  Spin,
  Divider,
  Row,
  Col,
  Select,
  Checkbox,
  Switch,
  Radio
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  MinusCircleOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import api from '../utils/api'
import Hint from '../components/common/Hint'
import { formatTz } from '../utils/timezone'

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 32px 24px;
  background: #fafbfc;

  @media (max-width: 820px) {
    padding: 20px 16px;
  }

  @media (max-width: 480px) {
    padding: 16px 12px;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 24px;
  }
`

const Title = styled.h2`
  margin: 0;
  font-size: 28px;
  font-weight: 300;
  color: #1a1a1a;
  letter-spacing: 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }

  @media (max-width: 480px) {
    font-size: 20px;
  }
`

const TierCard = styled(Card)`
  margin-bottom: 20px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  background: #ffffff;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .ant-card-body {
    padding: 24px;

    @media (max-width: 820px) {
      padding: 20px;
    }

    @media (max-width: 480px) {
      padding: 16px;
    }
  }
`

const TierHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
`

const TierInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;

  @media (max-width: 480px) {
    gap: 12px;
  }
`

const TierBadge = styled(Tag)`
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 600;
  border-radius: 12px;
  margin: 0;
  border: none;

  @media (max-width: 480px) {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }
`

const TierDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const TierName = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1d1d1f;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const TierDiscount = styled.div`
  font-size: 14px;
  color: #6e6e73;
  margin-top: 2px;
`

const DefaultBadge = styled(Tag)`
  background: #f5f5f7;
  color: #007aff;
  border: 1px solid #007aff;
  margin: 0;
  padding: 2px 8px;
  font-size: 12px;
  border-radius: 6px;
  font-weight: 500;
`

const Actions = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
    border-top: 1px solid #f5f5f7;
    padding-top: 16px;
  }

  .ant-btn {
    border-radius: 8px;
    height: 38px;
    padding: 0 16px;
    font-size: 14px;
    font-weight: 500;
    border: 1px solid #d2d2d7;
    color: #1d1d1f;
    background: #fff;
    transition: all 0.2s;

    &:hover {
      background: #f5f5f7;
      border-color: #86868b;
    }

    @media (max-width: 480px) {
      font-size: 13px;
      padding: 6px 12px;
      height: auto;
    }
  }

  .ant-btn-dangerous {
    border-color: #ff3b30;
    color: #ff3b30;

    &:hover {
      background: #ff3b30;
      color: white;
      border-color: #ff3b30;
    }
  }
`

// 到期提醒區塊的欄位標籤／說明文字樣式（與頁面其他表單一致，不另外開 styled）
const ReminderLabelStyle = {
  fontSize: 13.5,
  fontWeight: 500,
  color: '#4a4247',
  marginBottom: 7
}

const ReminderHintStyle = {
  fontSize: 12,
  color: '#9b9298',
  marginTop: 6,
  lineHeight: 1.6
}

/**
 * 可點擊插入的變數標籤。
 * 店家不用背 {客戶姓名} 這種寫法，也不用為了加一個變數重打整段文案。
 */
const VariableChips = ({ variables, onPick }) => {
  if (!variables || variables.length === 0) return null
  return (
    <div
      style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 9 }}
    >
      {variables.map((variable) => (
        <Tag
          key={variable.key}
          onClick={() => onPick(variable.token)}
          title={variable.description}
          style={{
            cursor: 'pointer',
            borderRadius: 20,
            padding: '2px 11px',
            fontSize: 12,
            color: '#8a4a5c',
            background: '#fdf7f9',
            border: '1px solid #f5dfe7',
            margin: 0
          }}
        >
          {variable.token}
        </Tag>
      ))}
    </div>
  )
}

const MembershipTierSettingsPage = () => {
  const { user } = useAuth()
  const businessId = user?.businessId
  const [tiers, setTiers] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingTier, setEditingTier] = useState(null)
  const [form] = Form.useForm()

  // 获取会员等级列表
  const fetchTiers = async () => {
    if (!businessId) return

    setLoading(true)
    try {
      const response = await api.get(
        `/api/business_B/${businessId}/membership-tiers`
      )
      if (response.data.success) {
        const tiersData = response.data.data?.tiers || []
        // 按 order 排序
        const sortedTiers = [...tiersData].sort((a, b) => a.order - b.order)
        setTiers(sortedTiers)
        setScope(
          response.data.data?.tierAccumulationScope === 'service'
            ? 'service'
            : 'all'
        )
      }
    } catch (error) {
      console.error('獲取會員等級失敗:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTiers()
  }, [businessId])

  /**
   * 會員累計金額的計入範圍。
   *   all     　整張結帳單（療程 + 產品 + 堂票售票）
   *   service 　只算療程與堂票售票，產品不計入
   * 多數店家的認知是產品業績不算會員累積，但這是各店自己的政策，做成設定。
   */
  const [scope, setScope] = useState('all')
  const [scopeSaving, setScopeSaving] = useState(false)

  const handleSaveScope = async (next) => {
    if (!businessId || next === scope) return
    const prev = scope
    setScope(next) // 先反映在畫面上，失敗再退回
    setScopeSaving(true)
    try {
      const res = await api.put(
        `/api/business_B/${businessId}/membership-accumulation-scope`,
        { scope: next }
      )
      message.success(res.data?.message || '已儲存')
    } catch (error) {
      setScope(prev)
      message.error(
        error.response?.data?.message || '儲存失敗，請稍後再試'
      )
    } finally {
      setScopeSaving(false)
    }
  }

  // ── 會員到期提醒 ──────────────────────────────────────────────
  // 會籍到期當天才自動續等／降等，客人事前不知情；這裡讓店家在到期前
  // 主動通知，文案自己寫（變數用中文，後端 shared/constants 有同一份）。
  const [reminder, setReminder] = useState(null)
  const [reminderMeta, setReminderMeta] = useState(null)
  const [reminderLoading, setReminderLoading] = useState(false)
  const [reminderSaving, setReminderSaving] = useState(false)
  const [reminderTesting, setReminderTesting] = useState(false)
  const titleInputRef = useRef(null)
  const contentInputRef = useRef(null)

  const fetchReminder = useCallback(async () => {
    if (!businessId) return
    setReminderLoading(true)
    try {
      const response = await api.get(
        `/api/business_B/${businessId}/membership-expiry-reminder`
      )
      if (response.data?.success) {
        const data = response.data.data
        setReminder(data.settings)
        setReminderMeta({
          variables: data.variables || [],
          defaults: data.defaults,
          eligibleTiers: data.eligibleTiers || [],
          limits: data.limits || { minDaysBefore: 1, maxDaysBefore: 180 },
          lastSentAt: data.lastSentAt,
          lastSentCount: data.lastSentCount
        })
      }
    } catch (error) {
      console.error('獲取會員到期提醒設定失敗:', error)
    } finally {
      setReminderLoading(false)
    }
  }, [businessId])

  useEffect(() => {
    fetchReminder()
  }, [fetchReminder])

  const updateReminder = (patch) =>
    setReminder((prev) => ({ ...(prev || {}), ...patch }))

  const updateReminderMessage = (patch) =>
    setReminder((prev) => ({
      ...(prev || {}),
      message: { ...(prev?.message || {}), ...patch }
    }))

  /**
   * 把變數插到游標位置。
   * 店家不該需要自己打 {客戶姓名}，更不該為了加一個變數把整段文案重打。
   */
  const insertVariable = (field, token) => {
    const ref = field === 'title' ? titleInputRef : contentInputRef
    const node =
      field === 'title'
        ? ref.current?.input
        : ref.current?.resizableTextArea?.textArea
    const current = reminder?.message?.[field] || ''

    if (!node || typeof node.selectionStart !== 'number') {
      updateReminderMessage({ [field]: current + token })
      return
    }

    const start = node.selectionStart
    const end = node.selectionEnd
    const next = current.slice(0, start) + token + current.slice(end)
    updateReminderMessage({ [field]: next })
    // 等 React 寫回 value 之後再把游標移到插入處後面
    setTimeout(() => {
      node.focus()
      node.selectionStart = node.selectionEnd = start + token.length
    }, 0)
  }

  const handleSaveReminder = async () => {
    if (!businessId || !reminder) return
    setReminderSaving(true)
    try {
      const response = await api.put(
        `/api/business_B/${businessId}/membership-expiry-reminder`,
        {
          enabled: reminder.enabled,
          daysBefore: reminder.daysBefore,
          message: reminder.message
        }
      )
      if (response.data?.success) {
        setReminder(response.data.data?.settings || reminder)
        message.success('會員到期提醒設定已儲存')
      }
    } catch (error) {
      console.error('儲存會員到期提醒設定失敗:', error)
      message.error(
        error.response?.data?.message || '儲存失敗，請稍後再試'
      )
    } finally {
      setReminderSaving(false)
    }
  }

  const handleTestReminder = async () => {
    if (!businessId || !reminder) return
    setReminderTesting(true)
    try {
      // 帶上目前畫面上的文案（可能還沒存檔），讓店家邊改邊測
      const response = await api.post(
        `/api/business_B/${businessId}/membership-expiry-reminder/test`,
        { message: reminder.message }
      )
      if (response.data?.success) {
        message.success(response.data.message || '測試訊息已寄出')
      }
    } catch (error) {
      console.error('發送測試訊息失敗:', error)
      message.error(
        error.response?.data?.message || '發送測試訊息失敗'
      )
    } finally {
      setReminderTesting(false)
    }
  }

  const handleResetReminderText = () => {
    const defaults = reminderMeta?.defaults?.message
    if (!defaults) return
    updateReminderMessage({
      title: defaults.title,
      content: defaults.content,
      buttonLabel: defaults.buttonLabel
    })
    message.success('已還原預設文案（記得按儲存）')
  }

  // 打开创建/编辑模态框
  const handleOpenModal = (tier = null) => {
    setEditingTier(tier)
    setModalVisible(true)
    // 立刻把目標等級資料寫進 Form（避免依賴 initialValues 在某些情境不重置）
    setTimeout(() => {
      form.resetFields()
      if (tier) {
        form.setFieldsValue({
          ...tier,
          isEnabled: tier.isEnabled !== false,
          isDefault: tier.isDefault || false,
          topupBonuses: (tier.topupBonuses || []).map((b) => ({
            amount: b.amount,
            bonus: b.bonus
          })),
          upgradeAmount: tier.upgradeAmount || 0,
          renewAmount: tier.renewAmount || 0,
          tierValidityDays: tier.tierValidityDays || 365,
          onUpgradeRule: tier.onUpgradeRule || 'reset',
          lookbackDays: tier.lookbackDays || 0,
          accumulationWindowDays: tier.accumulationWindowDays || 0,
          topupUpgradeAmount: tier.topupUpgradeAmount || 0
        })
      } else {
        form.setFieldsValue({
          discountRate: 100,
          color: '#1890ff',
          order: tiers.length,
          isDefault: false,
          isEnabled: true,
          topupBonuses: [],
          upgradeAmount: 0,
          renewAmount: 0,
          tierValidityDays: 365,
          onUpgradeRule: 'reset',
          lookbackDays: 0,
          accumulationWindowDays: 0,
          topupUpgradeAmount: 0
        })
      }
    }, 0)
  }

  // 关闭模态框
  const handleCloseModal = () => {
    setModalVisible(false)
    setEditingTier(null)
    form.resetFields()
  }

  // 保存会员等级
  const handleSave = async () => {
    try {
      const values = await form.validateFields()

      if (!businessId) {
        message.error('未能取得商家資訊')
        return
      }

      // 如果设置为默认，需要先取消其他默认
      if (values.isDefault) {
        const otherDefaults = tiers.filter(
          (t) => t.isDefault && (!editingTier || t._id !== editingTier._id)
        )
        if (otherDefaults.length > 0) {
          // 先取消其他默认
          for (const tier of otherDefaults) {
            await api.put(
              `/api/business_B/${businessId}/membership-tiers/${tier._id}`,
              {
                ...tier,
                isDefault: false
              }
            )
          }
        }
      }

      // 處理等級儲值回饋（topupBonuses）
      const topupBonuses = (values.topupBonuses || [])
        .filter((item) => item && item.amount && item.bonus)
        .map((item) => ({
          amount: Number(item.amount) || 0,
          bonus: Number(item.bonus) || 0
        }))

      // 自動升降等欄位
      const tierAutoFields = {
        upgradeAmount: Number(values.upgradeAmount) || 0,
        renewAmount: Number(values.renewAmount) || 0,
        tierValidityDays: Number(values.tierValidityDays) || 0,
        onUpgradeRule: values.onUpgradeRule || 'reset',
        lookbackDays: Number(values.lookbackDays) || 0,
        accumulationWindowDays: Number(values.accumulationWindowDays) || 0,
        topupUpgradeAmount: Number(values.topupUpgradeAmount) || 0
      }

      if (editingTier) {
        // 更新
        await api.put(
          `/api/business_B/${businessId}/membership-tiers/${editingTier._id}`,
          {
            name: values.name,
            discountRate: values.discountRate,
            color: values.color,
            order: values.order,
            isDefault: values.isDefault || false,
            isEnabled: values.isEnabled !== false,
            topupBonuses: topupBonuses,
            ...tierAutoFields
          }
        )
        message.success('會員等級更新成功')
      } else {
        // 创建
        await api.post(`/api/business_B/${businessId}/membership-tiers`, {
          name: values.name,
          discountRate: values.discountRate,
          color: values.color,
          order: values.order,
          isDefault: values.isDefault || false,
          isEnabled: values.isEnabled !== false,
          topupBonuses: topupBonuses,
          ...tierAutoFields
        })
        message.success('會員等級創建成功')
      }

      handleCloseModal()
      fetchTiers()
    } catch (error) {
      console.error('保存會員等級失敗:', error)
      message.error(
        error.response?.data?.message || '保存會員等級失敗，請稍後再試'
      )
    }
  }

  // 删除会员等级
  const handleDelete = async (tier) => {
    try {
      await api.delete(
        `/api/business_B/${businessId}/membership-tiers/${tier._id}`
      )
      message.success('會員等級刪除成功')
      fetchTiers()
    } catch (error) {
      console.error('刪除會員等級失敗:', error)
      const errorMessage =
        error.response?.data?.message || '刪除失敗，可能有客戶正在使用此等級'
      message.error(errorMessage)
    }
  }

  // 切换启用状态
  const handleToggleEnabled = async (tier) => {
    try {
      await api.put(
        `/api/business_B/${businessId}/membership-tiers/${tier._id}`,
        {
          ...tier,
          isEnabled: !tier.isEnabled
        }
      )
      message.success(`會員等級已${tier.isEnabled ? '停用' : '啟用'}`)
      fetchTiers()
    } catch (error) {
      console.error('更新會員等級失敗:', error)
      message.error('更新失敗，請稍後再試')
    }
  }

  return (
    <PageWrapper>
      <Header>
        <Title>會員等級設置</Title>
        <Button
          type='primary'
          icon={<PlusOutlined />}
          onClick={() => handleOpenModal()}
          style={{
            borderRadius: 10,
            padding: '0 20px',
            fontSize: 15,
            fontWeight: 500,
            background: '#007aff',
            border: 'none',
            boxShadow: '0 2px 8px rgba(0, 122, 255, 0.3)'
          }}
        >
          添加會員等級
        </Button>
      </Header>

      {loading ? (
        <Card
          style={{
            borderRadius: 12,
            border: '1px solid #e5e7eb',
            boxShadow: 0,
            background: '#ffffff'
          }}
        >
          <div
            style={{
              textAlign: 'center',
              padding: '80px 20px'
            }}
          >
            <Spin size='large' />
          </div>
        </Card>
      ) : tiers.length === 0 ? (
        <Card
          style={{
            borderRadius: 12,
            border: '1px solid #e5e7eb',
            boxShadow: 0,
            background: '#ffffff'
          }}
        >
          <Empty
            description={
              <span style={{ color: '#6b7280', fontSize: 14 }}>
                暫無會員等級
              </span>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
              style={{
                borderRadius: 10,
                padding: '0 24px',
                fontSize: 15,
                fontWeight: 500,
                background: '#007aff',
                border: 'none',
                boxShadow: '0 2px 8px rgba(0, 122, 255, 0.3)'
              }}
            >
              創建第一個會員等級
            </Button>
          </Empty>
        </Card>
      ) : (
        <div>
          {tiers.map((tier) => (
            <TierCard key={tier._id}>
              <TierHeader>
                <TierInfo>
                  <TierBadge
                    style={{
                      background: tier.color || '#1890ff',
                      color: tier.color === '#ffd700' ? '#000' : '#fff'
                    }}
                  >
                    {tier.name ? tier.name[0] : 'V'}
                  </TierBadge>
                  <TierDetails>
                    <TierName>
                      {tier.name}
                      {tier.isDefault && <DefaultBadge>默認等級</DefaultBadge>}
                      {!tier.isEnabled && (
                        <Tag
                          style={{
                            background: '#f5f5f7',
                            color: '#6e6e73',
                            border: 'none',
                            margin: 0,
                            padding: '4px 10px',
                            fontSize: 12,
                            borderRadius: 6,
                            fontWeight: 500
                          }}
                        >
                          已停用
                        </Tag>
                      )}
                    </TierName>
                    <TierDiscount>
                      折扣率：{tier.discountRate}%（
                      {tier.discountRate === 100
                        ? '無折扣'
                        : `${tier.discountRate}折`}
                      ）
                    </TierDiscount>
                    {tier.topupBonuses && tier.topupBonuses.length > 0 && (
                      <div
                        style={{
                          marginTop: 8,
                          fontSize: 13,
                          color: '#666'
                        }}
                      >
                        等級儲值回饋：
                        {tier.topupBonuses.map((bonus, idx) => (
                          <span key={idx}>
                            {idx > 0 && '、'}
                            儲值 {Number(
                              bonus.amount || 0
                            ).toLocaleString()} 送{' '}
                            {Number(bonus.bonus || 0).toLocaleString()}
                          </span>
                        ))}
                      </div>
                    )}
                  </TierDetails>
                </TierInfo>
                <Actions>
                  <Button
                    icon={
                      tier.isEnabled ? <CloseOutlined /> : <CheckOutlined />
                    }
                    onClick={() => handleToggleEnabled(tier)}
                    title={tier.isEnabled ? '停用' : '啟用'}
                    style={{
                      color: tier.isEnabled ? '#ff9500' : '#34c759'
                    }}
                  >
                    {tier.isEnabled ? '停用' : '啟用'}
                  </Button>
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => handleOpenModal(tier)}
                  >
                    編輯
                  </Button>
                  <Popconfirm
                    title='確定要刪除此會員等級嗎？'
                    description='如果有客戶正在使用此等級，將無法刪除'
                    onConfirm={() => handleDelete(tier)}
                    okText='確定'
                    cancelText='取消'
                    okButtonProps={{
                      style: {
                        background: '#ff3b30',
                        border: 'none',
                        borderRadius: 8
                      }
                    }}
                  >
                    <Button danger icon={<DeleteOutlined />}>
                      刪除
                    </Button>
                  </Popconfirm>
                </Actions>
              </TierHeader>
            </TierCard>
          ))}
        </div>
      )}

      {/* 累計金額的計入範圍 */}
      <TierCard style={{ marginTop: 8 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#1d1d1f' }}>
          消費累計的計算範圍
        </div>
        <div
          style={{
            fontSize: 13,
            color: '#6b7280',
            marginTop: 6,
            lineHeight: 1.8
          }}
        >
          決定客人結帳時，哪些金額要計入「升等／續等」的累計。
          儲值另有自己的門檻設定，不受這裡影響。
        </div>
        <Radio.Group
          value={scope}
          disabled={scopeSaving}
          onChange={(e) => handleSaveScope(e.target.value)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            marginTop: 14
          }}
        >
          <Radio value='all'>
            全部消費
            <span style={{ color: '#6b7280', fontSize: 13 }}>
              　療程 + 產品 + 堂票售票
            </span>
          </Radio>
          <Radio value='service'>
            不含產品
            <span style={{ color: '#6b7280', fontSize: 13 }}>
              　只算療程與堂票售票；線上商店的訂單也不計入
            </span>
          </Radio>
        </Radio.Group>
        <div
          style={{
            fontSize: 12,
            color: '#8a6430',
            background: '#fffaf2',
            border: '1px solid #f3e3c6',
            borderRadius: 8,
            padding: '8px 12px',
            marginTop: 14,
            lineHeight: 1.8
          }}
        >
          改設定只影響之後的結帳，已經累積的金額不會回頭重算 ——
          否則客人的等級會突然掉下去。
        </div>
      </TierCard>

      {/* 會員到期提醒 */}
      <TierCard style={{ marginTop: 8 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            paddingBottom: 14,
            borderBottom: '1px solid #f0ecee',
            marginBottom: 20
          }}
        >
          <div>
            <div style={{ fontSize: 17, fontWeight: 500, color: '#1a1a1a' }}>
              會員到期提醒
            </div>
            <div style={{ fontSize: 12.5, color: '#948b91', marginTop: 3 }}>
              會籍到期前主動通知客人，提醒他們還差多少可以續等
            </div>
          </div>
          <Switch
            checked={reminder?.enabled === true}
            loading={reminderLoading}
            onChange={(checked) => updateReminder({ enabled: checked })}
            style={
              reminder?.enabled ? { background: '#d5728a' } : undefined
            }
          />
        </div>

        {reminderLoading && !reminder ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <Spin />
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 20 }}>
              <div style={ReminderLabelStyle}>提前幾天發送</div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap'
                }}
              >
                <InputNumber
                  value={reminder?.daysBefore}
                  onChange={(value) => updateReminder({ daysBefore: value })}
                  min={reminderMeta?.limits?.minDaysBefore || 1}
                  max={reminderMeta?.limits?.maxDaysBefore || 180}
                  style={{ width: 110 }}
                />
                <span style={{ fontSize: 13.5, color: '#6b6067' }}>
                  天 ── 每位會員每個會籍週期只會收到一次
                </span>
              </div>
              <div style={ReminderHintStyle}>
                設 30 天：會籍 10/09 到期的會員，會在 09/09 收到通知。
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={ReminderLabelStyle}>通知標題</div>
              <Input
                ref={titleInputRef}
                value={reminder?.message?.title || ''}
                onChange={(e) =>
                  updateReminderMessage({ title: e.target.value })
                }
                placeholder='例如：{客戶姓名}，您的{會員等級}資格 {剩餘天數} 天後到期'
              />
              <VariableChips
                variables={reminderMeta?.variables}
                onPick={(token) => insertVariable('title', token)}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={ReminderLabelStyle}>通知內容</div>
              <Input.TextArea
                ref={contentInputRef}
                value={reminder?.message?.content || ''}
                onChange={(e) =>
                  updateReminderMessage({ content: e.target.value })
                }
                rows={6}
                placeholder='寫給客人看的內容，可以插入下方變數'
              />
              <VariableChips
                variables={reminderMeta?.variables}
                onPick={(token) => insertVariable('content', token)}
              />
              <div style={ReminderHintStyle}>
                點變數即可插入游標位置。發送時會自動換成每位客人的實際資料。
              </div>
            </div>

            <Divider style={{ margin: '24px 0 20px' }} />

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 14,
                marginBottom: 14
              }}
            >
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 500, color: '#4a4247' }}>
                  訊息底下顯示行動按鈕
                </div>
                <div style={{ fontSize: 12, color: '#9b9298', marginTop: 2 }}>
                  關掉的話只會有純文字，不會出現按鈕
                </div>
              </div>
              <Switch
                checked={reminder?.message?.showBookingButton !== false}
                onChange={(checked) =>
                  updateReminderMessage({ showBookingButton: checked })
                }
                style={
                  reminder?.message?.showBookingButton !== false
                    ? { background: '#d5728a' }
                    : undefined
                }
              />
            </div>

            {reminder?.message?.showBookingButton !== false && (
              <div style={{ marginBottom: 20 }}>
                <div style={ReminderLabelStyle}>按鈕文字</div>
                <Input
                  value={reminder?.message?.buttonLabel || ''}
                  onChange={(e) =>
                    updateReminderMessage({ buttonLabel: e.target.value })
                  }
                  maxLength={20}
                  placeholder='立即預約'
                  style={{ maxWidth: 260 }}
                />
                <div style={ReminderHintStyle}>
                  按鈕連結固定指向您的店家頁面。留白等同「立即預約」。
                </div>
              </div>
            )}

            <Hint tone='info'>
              發送管道：<b>LINE 優先</b>，客人沒綁 LINE 就改寄 Email，兩邊都沒有就跳過。
              與堂票到期提醒同一套發送邏輯。
            </Hint>

            <div style={{ height: 10 }} />

            {reminderMeta?.eligibleTiers?.length > 0 ? (
              <Hint tone='warning' title='只有這些等級會發出提醒'>
                需要同時設了「會籍效期」與「續等門檻」的等級才會通知，而且只發給
                <b> 還沒達到續等門檻 </b>
                的會員（已達標的到期會自動續等，不打擾）。目前符合的有：
                {reminderMeta.eligibleTiers
                  .map(
                    (t) =>
                      `${t.name}（效期 ${t.tierValidityDays} 天、門檻 $${Number(
                        t.renewAmount
                      ).toLocaleString()}）`
                  )
                  .join('、')}
              </Hint>
            ) : (
              <Hint tone='warning' title='目前沒有等級會發出提醒'>
                提醒只會發給「會籍效期 &gt; 0 且續等門檻 &gt; 0」的等級會員。
                請先到上方的等級設定填好「會籍效期天數」與「續等門檻」。
              </Hint>
            )}

            {reminderMeta?.lastSentAt && (
              <div style={{ ...ReminderHintStyle, marginTop: 12 }}>
                最近一次發送：
                {formatTz(reminderMeta.lastSentAt, 'YYYY/MM/DD HH:mm')}
                （{reminderMeta.lastSentCount} 則）
              </div>
            )}

            <div
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'center',
                flexWrap: 'wrap',
                paddingTop: 22,
                marginTop: 16,
                borderTop: '1px solid #f0ecee'
              }}
            >
              <Button
                type='primary'
                loading={reminderSaving}
                onClick={handleSaveReminder}
                style={{
                  borderRadius: 10,
                  background: '#d5728a',
                  border: 'none',
                  boxShadow: '0 2px 8px rgba(213, 114, 138, 0.28)'
                }}
              >
                儲存設定
              </Button>
              <Button
                loading={reminderTesting}
                onClick={handleTestReminder}
                style={{
                  borderRadius: 10,
                  color: '#d5728a',
                  borderColor: '#eccdd7'
                }}
              >
                發送測試訊息給自己
              </Button>
              <span style={{ flex: 1 }} />
              <Button
                type='text'
                onClick={handleResetReminderText}
                style={{ color: '#948b91' }}
              >
                還原預設文案
              </Button>
            </div>
          </>
        )}
      </TierCard>

      {/* 创建/编辑模态框 */}
      <Modal
        key={modalVisible ? editingTier?._id || 'new' : 'closed'}
        title={
          <span style={{ fontSize: 20, fontWeight: 600, color: '#1d1d1f' }}>
            {editingTier ? '編輯會員等級' : '創建會員等級'}
          </span>
        }
        open={modalVisible}
        onOk={handleSave}
        onCancel={handleCloseModal}
        okText='保存'
        cancelText='取消'
        width={600}
        destroyOnClose
        forceRender={false}
        styles={{
          body: {
            padding: '24px'
          }
        }}
        okButtonProps={{
          style: {
            borderRadius: 10,
            padding: '0 24px',
            fontSize: 15,
            fontWeight: 500,
            background: '#007aff',
            border: 'none'
          }
        }}
        cancelButtonProps={{
          style: {
            borderRadius: 10,
            padding: '0 24px',
            fontSize: 15,
            fontWeight: 500,
            border: '1px solid #d2d2d7',
            color: '#1d1d1f'
          }
        }}
      >
        <Form
          form={form}
          layout='vertical'
          initialValues={
            editingTier
              ? {
                  ...editingTier,
                  isEnabled: editingTier.isEnabled !== false,
                  isDefault: editingTier.isDefault || false,
                  topupBonuses: (editingTier.topupBonuses || []).map(
                    (bonus) => ({
                      amount: bonus.amount,
                      bonus: bonus.bonus
                    })
                  )
                }
              : {
                  discountRate: 100,
                  color: '#1890ff',
                  order: tiers.length,
                  isDefault: false,
                  isEnabled: true,
                  topupBonuses: [],
                  upgradeAmount: 0,
                  renewAmount: 0,
                  tierValidityDays: 365,
                  onUpgradeRule: 'reset',
                  lookbackDays: 0,
                  accumulationWindowDays: 0,
                  topupUpgradeAmount: 0
                }
          }
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='name'
                label={
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: '#1d1d1f'
                    }}
                  >
                    等級名稱
                  </span>
                }
                rules={[{ required: true, message: '請輸入等級名稱' }]}
              >
                <Input
                  placeholder='例如：金卡、銀卡、普通會員'
                  style={{
                    borderRadius: 10,
                    fontSize: 15,
                    height: 34,
                    border: '1px solid #d2d2d7',
                    padding: '0 16px'
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name='discountRate'
                label={
                  <span
                    style={{ fontSize: 14, fontWeight: 500, color: '#1d1d1f' }}
                  >
                    折扣率（%）
                  </span>
                }
                rules={[
                  { required: true, message: '請輸入折扣率' },
                  {
                    type: 'number',
                    min: 1,
                    max: 100,
                    message: '折扣率必須在 1-100 之間'
                  }
                ]}
              >
                <InputNumber
                  min={1}
                  max={100}
                  style={{
                    width: '100%',
                    borderRadius: 10,
                    fontSize: 15,
                    border: '1px solid #d2d2d7'
                  }}
                  placeholder='例如：85 表示 85 折'
                  formatter={(value) => `${value}%`}
                  parser={(value) => value.replace('%', '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='color'
                label={
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: '#1d1d1f'
                    }}
                  >
                    顯示顏色
                  </span>
                }
                rules={[{ required: true, message: '請選擇顏色' }]}
              >
                <ColorPicker
                  showText
                  format='hex'
                  style={{
                    width: '100%',
                    borderRadius: 10,
                    border: '1px solid #d2d2d7',
                    padding: '4px'
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name='order'
                label={
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: '#1d1d1f'
                    }}
                  >
                    排序順序
                  </span>
                }
                rules={[{ required: true, message: '請輸入排序順序' }]}
              >
                <InputNumber
                  min={0}
                  style={{
                    width: '100%',
                    borderRadius: 10,
                    fontSize: 15,
                    border: '1px solid #d2d2d7'
                  }}
                  placeholder='數字越小越靠前'
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name='isDefault' valuePropName='checked'>
            <Checkbox style={{ fontSize: 14, color: '#1d1d1f' }}>
              設為默認等級
            </Checkbox>
          </Form.Item>

          <Form.Item name='isEnabled' valuePropName='checked'>
            <Checkbox style={{ fontSize: 14, color: '#1d1d1f' }}>
              啟用此等級
            </Checkbox>
          </Form.Item>

          <Divider style={{ margin: '24px 0' }} />

          <div style={{ marginBottom: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 500, color: '#1d1d1f' }}>
              自動升降等規則
            </span>
            <div
              style={{
                fontSize: 12,
                color: '#8e8e93',
                marginTop: 4,
                marginBottom: 12
              }}
            >
              設置後，系統會在會員結帳完成時即時累計實付金額，達標自動升等；
              會籍到期由每日 06:00 排程處理續等／降等。所有欄位填 0 = 不啟用該規則。
            </div>
          </div>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='upgradeAmount'
                label={
                  <span
                    style={{ fontSize: 14, fontWeight: 500, color: '#1d1d1f' }}
                  >
                    升等門檻（累計實付）
                  </span>
                }
                tooltip='累計實付金額達此值即升至此等級。0 = 此等級不參與自動升等。可搭配「累積窗口天數」做「N 個月內累計達標」規則'
              >
                <InputNumber
                  min={0}
                  step={1000}
                  style={{ width: '100%', borderRadius: 10 }}
                  placeholder='例如：30000'
                  formatter={(v) => (v ? `$ ${Number(v).toLocaleString()}` : '')}
                  parser={(v) => (v ? v.replace(/\D/g, '') : 0)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='renewAmount'
                label={
                  <span
                    style={{ fontSize: 14, fontWeight: 500, color: '#1d1d1f' }}
                  >
                    續等門檻
                  </span>
                }
                tooltip='會籍到期時累計達此值可維持等級。0 = 不需門檻（永遠續等）'
              >
                <InputNumber
                  min={0}
                  step={1000}
                  style={{ width: '100%', borderRadius: 10 }}
                  placeholder='例如：20000'
                  formatter={(v) => (v ? `$ ${Number(v).toLocaleString()}` : '')}
                  parser={(v) => (v ? v.replace(/\D/g, '') : 0)}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='accumulationWindowDays'
                label={
                  <span
                    style={{ fontSize: 14, fontWeight: 500, color: '#1d1d1f' }}
                  >
                    累積窗口天數
                  </span>
                }
                tooltip='從會員「累積起算日」起算 N 天，沒達升等門檻就累計歸零重算（例：120=4個月內未達標→重算）。0 = 不歸零，無限期累積'
              >
                <InputNumber
                  min={0}
                  max={3650}
                  style={{ width: '100%', borderRadius: 10 }}
                  placeholder='例如：120'
                  addonAfter='天'
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='topupUpgradeAmount'
                label={
                  <span
                    style={{ fontSize: 14, fontWeight: 500, color: '#1d1d1f' }}
                  >
                    儲值升等門檻（單筆）
                  </span>
                }
                tooltip='單筆儲值金額達此值即升至此等級。與消費累積為 OR 關係（任一達標即升等）。0 = 儲值不觸發此級升等'
              >
                <InputNumber
                  min={0}
                  step={1000}
                  style={{ width: '100%', borderRadius: 10 }}
                  placeholder='例如：10000'
                  formatter={(v) => (v ? `$ ${Number(v).toLocaleString()}` : '')}
                  parser={(v) => (v ? v.replace(/\D/g, '') : 0)}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='tierValidityDays'
                label={
                  <span
                    style={{ fontSize: 14, fontWeight: 500, color: '#1d1d1f' }}
                  >
                    會籍效期（天）
                  </span>
                }
                tooltip='會員獲得此等級後可維持的天數。常見：365（一年）。0 = 永久不到期'
              >
                <InputNumber
                  min={0}
                  max={3650}
                  style={{ width: '100%', borderRadius: 10 }}
                  placeholder='例如：365'
                  addonAfter='天'
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='onUpgradeRule'
                label={
                  <span
                    style={{ fontSize: 14, fontWeight: 500, color: '#1d1d1f' }}
                  >
                    升等後累計處理
                  </span>
                }
                tooltip='升至此等級時，會員的累計實付金額要怎麼處理'
              >
                <Select
                  options={[
                    { value: 'reset', label: '歸零重新計算（建議）' },
                    { value: 'continue', label: '不歸零，繼續累計' },
                    { value: 'lookback', label: '追溯前 N 天（暫等同歸零）' }
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '24px 0' }} />

          <div style={{ marginBottom: 16 }}>
            <span style={{ fontSize: 14, fontWeight: 500, color: '#1d1d1f' }}>
              等級儲值回饋設置
            </span>
            <div
              style={{
                fontSize: 12,
                color: '#8e8e93',
                marginTop: 4,
                marginBottom: 12
              }}
            >
              設置各儲值金額對應的回饋（贈送金額），例如：儲值 10,000 元送 1,000 元
            </div>
          </div>

          <Form.List name='topupBonuses' initialValue={[]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space
                    key={key}
                    style={{ display: 'flex', marginBottom: 8, width: '100%' }}
                    align='baseline'
                  >
                    <Form.Item
                      {...restField}
                      name={[name, 'amount']}
                      rules={[
                        { required: true, message: '請輸入儲值金額' },
                        {
                          type: 'number',
                          min: 1,
                          message: '儲值金額必須大於 0'
                        }
                      ]}
                      style={{ flex: 1 }}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder='儲值金額'
                        prefix='NT$'
                        min={1}
                        precision={0}
                        formatter={(value) =>
                          value
                            ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            : ''
                        }
                        parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                      />
                    </Form.Item>
                    <span style={{ margin: '0 8px', color: '#8e8e93' }}>
                      送
                    </span>
                    <Form.Item
                      {...restField}
                      name={[name, 'bonus']}
                      rules={[
                        { required: true, message: '請輸入贈送金額' },
                        {
                          type: 'number',
                          min: 0,
                          message: '贈送金額必須大於等於 0'
                        }
                      ]}
                      style={{ flex: 1 }}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder='贈送金額'
                        prefix='NT$'
                        min={0}
                        precision={0}
                        formatter={(value) =>
                          value
                            ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            : ''
                        }
                        parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                      />
                    </Form.Item>
                    <Button
                      type='text'
                      danger
                      icon={<MinusCircleOutlined />}
                      onClick={() => remove(name)}
                      style={{ color: '#ff3b30' }}
                    />
                  </Space>
                ))}
                <Button
                  type='dashed'
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                  style={{
                    marginTop: 8,
                    borderRadius: 10,
                    border: '1px dashed #d2d2d7',
                    color: '#007aff'
                  }}
                >
                  添加等級儲值回饋檔位
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </PageWrapper>
  )
}

export default MembershipTierSettingsPage
